/**
 * useIntroductions Hook
 * Phase 6 Part 3: Warm Introduction Requests
 * React hooks for managing introduction requests
 */

import { useState, useEffect, useCallback } from 'react';
import { introductionService } from '../services/introductionService';

/**
 * Hook for seller's introduction requests
 * @param {string} sellerId - Seller UUID
 */
export function useSellerIntroductions(sellerId) {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!sellerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [requestsData, statsData] = await Promise.all([
        introductionService.getSellerRequests(sellerId),
        introductionService.getSellerStats(sellerId)
      ]);
      setRequests(requestsData);
      setStats(statsData);
      setError(null);
    } catch (err) {
      console.error('Error loading introduction requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create new introduction request
  const createRequest = async (params) => {
    try {
      const newRequest = await introductionService.createRequest({
        ...params,
        sellerId
      });
      setRequests(prev => [newRequest, ...prev]);
      return newRequest;
    } catch (err) {
      console.error('Error creating request:', err);
      throw err;
    }
  };

  // Cancel a pending request
  const cancelRequest = async (requestId) => {
    try {
      await introductionService.cancelRequest(requestId);
      setRequests(prev => prev.map(r =>
        r.id === requestId ? { ...r, status: 'expired' } : r
      ));
    } catch (err) {
      console.error('Error canceling request:', err);
      throw err;
    }
  };

  return {
    requests,
    stats,
    loading,
    error,
    refresh: loadData,
    createRequest,
    cancelRequest
  };
}

/**
 * Hook for connector's introduction requests
 * @param {string} connectorUserId - Connector user UUID
 */
export function useConnectorIntroductions(connectorUserId) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!connectorUserId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await introductionService.getConnectorRequests(connectorUserId);
      setRequests(data);
      setError(null);
    } catch (err) {
      console.error('Error loading connector requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [connectorUserId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Respond to a request (accept/decline)
  const respondToRequest = async (requestId, status, response = null) => {
    try {
      const updated = await introductionService.respondToRequest(requestId, status, response);
      setRequests(prev => prev.map(r => r.id === requestId ? updated : r));
      return updated;
    } catch (err) {
      console.error('Error responding to request:', err);
      throw err;
    }
  };

  // Mark introduction as made
  const markIntroductionMade = async (requestId) => {
    try {
      const updated = await introductionService.markIntroductionMade(requestId);
      setRequests(prev => prev.map(r => r.id === requestId ? updated : r));
      return updated;
    } catch (err) {
      console.error('Error marking introduction:', err);
      throw err;
    }
  };

  return {
    requests,
    loading,
    error,
    refresh: loadData,
    respondToRequest,
    markIntroductionMade
  };
}

/**
 * Hook for checking existing introduction request
 * @param {string} sellerId - Seller UUID
 * @param {string} buyerId - Buyer UUID
 */
export function useExistingIntroduction(sellerId, buyerId) {
  const [existingRequest, setExistingRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function check() {
      if (!sellerId || !buyerId) {
        setLoading(false);
        return;
      }

      try {
        const existing = await introductionService.checkExistingRequest(sellerId, buyerId);
        setExistingRequest(existing);
      } catch (err) {
        console.error('Error checking existing request:', err);
      } finally {
        setLoading(false);
      }
    }

    check();
  }, [sellerId, buyerId]);

  return { existingRequest, loading };
}

export default {
  useSellerIntroductions,
  useConnectorIntroductions,
  useExistingIntroduction
};
